import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { AsyncPipe, DatePipe } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnInit, Output, VERSION, forwardRef, inject, computed, signal, effect } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { ComboBoxModule } from '@progress/kendo-angular-dropdowns';
import { GridModule, RowClassArgs, SelectAllCheckboxState, SelectableMode, SelectableSettings } from '@progress/kendo-angular-grid';
import { CheckBoxModule, TextBoxModule } from '@progress/kendo-angular-inputs';
import { FloatingLabelModule } from '@progress/kendo-angular-label';
import { CommonService } from '../../../shared/services/common.service';
import { ActivityInfo } from '../../calendars/calendars.models';
import { CalendarsService } from '../../calendars/calendars.service';
import { CampaignAssignerService } from './campaign-assigner.service';
import { CampaignsService } from '../campaigns.service';
import { injectQuery, QueryClient } from '@tanstack/angular-query-experimental';

export interface CampaignAssignerDialogData
{
  clientId: number;
  excludeIds?: number[];
}

@Component({
  selector: 'campaign-assigner',
  templateUrl: './campaign-assigner.component.html',
  styleUrls: ['./campaign-assigner.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CampaignAssignerComponent),
      multi: true
    },
    CampaignAssignerService
  ],
  standalone: true,
  imports: [
    MatProgressSpinnerModule,
    CdkDrag,
    CdkDragHandle,
    MatDialogTitle,
    FloatingLabelModule,
    TextBoxModule,
    ComboBoxModule,
    GridModule,
    ButtonModule,
    CheckBoxModule,
    DatePipe,
    MatDialogActions,
    MatButtonModule
  ]
})
export class CampaignAssignerComponent implements OnInit, ControlValueAccessor
{
  // Signals
  private clientId = signal<number>(0);
  private excludedIds = signal<Set<number>>(new Set<number>());
  rowsSelected = signal<ActivityInfo[]>([]);

  // Select-all state reflects only visible rows
  selectAllState = computed<SelectAllCheckboxState>(() =>
  {
    const selected = this.rowsSelected().length;
    const total = this.visibleActivities().length;
    if (total === 0 || selected === 0) return 'unchecked';
    if (selected < total) return 'indeterminate';
    return 'checked';
  });

  public mode: SelectableMode = 'multiple';
  public selectableSettings: SelectableSettings = {
    checkboxOnly: true,
    enabled: true,
    mode: this.mode,
    drag: false,
  };

  public unassignedActivitiesQuery = injectQuery(() => ({
    enabled: this.clientId() > 0,
    queryKey: ['unassigned-activities', this.clientId()],
    queryFn: () => this.campaignsService.getUnassignedActivitiesForClient(this.clientId()),
    // Keep cached data (incl. optimistic edits) while dialog is open
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 60000 // 60s is enough for the assigner session; adjust as needed
  }));

  // Visible list excludes anything already on the campaign/editor
  public visibleActivities = computed<ActivityInfo[]>(
    () => this.unassignedActivities().filter(a => !this.excludedIds().has(a.Id))
  );

  // Expose a plain signal for template/logic (mirrors selected data)
  public unassignedActivities = computed<ActivityInfo[]>(() => this.unassignedActivitiesQuery.data() ?? []);

  private _isDisabled = false;

  // CVA noop outputs to preserve usage
  @Output() valueChange: EventEmitter<any> = new EventEmitter<any>();

  version = VERSION;

  @Input() get isDisabled(): boolean { return this._isDisabled; }
  set isDisabled(value: boolean) { this._isDisabled = value; }

  // Allow clientId via @Input when not using dialog
  @Input() set client(id: number | null | undefined)
  {
    const v = Number(id) || 0;
    if (v !== this.clientId()) this.clientId.set(v);
  }

  // Allow exclude list via @Input when not using dialog
  @Input() set exclude(ids: number[] | null | undefined)
  {
    const set = new Set((ids ?? []).map(Number));
    this.excludedIds.set(set);
  }

  private onTouchedCallback: () => {};
  private propagateChange = (value: any) => { };

  public commonService = inject(CommonService);
  public campaignAssignerService = inject(CampaignAssignerService);
  private calendarsService = inject(CalendarsService);
  private queryClient = inject(QueryClient);

  constructor(
    public campaignsService: CampaignsService,
    private dialogRef: MatDialogRef<CampaignAssignerComponent>,
    @Inject(MAT_DIALOG_DATA) private data: CampaignAssignerDialogData
  )
  {
    if (data)
    {
      const cid = Number((data as CampaignAssignerDialogData).clientId) || 0;
      if (cid) this.clientId.set(cid);
      const ids = (data as CampaignAssignerDialogData).excludeIds ?? [];
      this.excludedIds.set(new Set(ids.map(Number)));
    }
  }

  ngOnInit(): void { }
  ngOnDestroy(): void { }

  // Keep service mirror up to date (if other parts of app still read it)
  private _syncUnassigned = effect(() =>
  {
    this.campaignAssignerService.unassignedActivitiesForClient = this.visibleActivities();
  });

  public rowCallback = (context: RowClassArgs) =>
    this.isRowSelected(context.dataItem.Id) ? { selected: true } : { unselected: true };

  isRowSelected(id: number)
  {
    return this.rowsSelected().some(r => r.Id === id);
  }

  public onSelectAllChange(checked: boolean | SelectAllCheckboxState): void
  {
    const isChecked = checked === true || checked === 'checked';
    if (isChecked)
    {
      this.rowsSelected.set([...this.visibleActivities()]);
    } else
    {
      this.rowsSelected.set([]);
    }
  }

  public selectionChange(e: any)
  {
    // Handle only DOM checkbox change events
    const input = e?.target as HTMLInputElement | undefined;
    if (!input) return;

    const id = +input.id;
    if (input.checked)
    {
      const row = this.visibleActivities().find(r => r.Id === id);
      if (row && !this.isRowSelected(id))
      {
        this.rowsSelected.update(arr => [...arr, row]);
      }
    } else
    {
      this.rowsSelected.update(arr => arr.filter(r => r.Id !== id));
    }
  }

  assignActivities(event: any)
  {
    const selectedActivities = this.rowsSelected();

    this.dialogRef.close({ selectedActivities, clientId: this.clientId() });
  }


  onCancelClick() { this.dialogRef.close(); }

  writeValue(value: any): void { }
  registerOnChange(fn: any): void { this.propagateChange = fn; }
  registerOnTouched(fn: any): void { this.onTouchedCallback = fn; }
  setDisabledState?(disabled: boolean): void { this._isDisabled = disabled; }
}
